import { z } from 'zod';
import type { FeeAuthorization } from '~/features/employer/billing/types';
import { getData, patchData, postData } from '~/utils/service';
import {
  B2BListingResponseSchema,
  B2BMessageSchema,
  B2BNegotiationChainRowSchema,
  B2BOpportunitySchema,
} from './schemas';
import type {
  B2BListingResponse,
  B2BMessage,
  B2BNegotiationChainRow,
  B2BOpportunity,
  B2BOpportunityFilter,
  B2BResponsesSide,
} from './types';

/**
 * Backend contract for GET /hlx/job-responses/b2b-opportunities.
 * Controller returns the opportunities array at the top level today.
 * The schema is tolerant of either shape so the feature survives a
 * future wrap into `{ items: [...] }` without a flag day.
 */
// TODO(MCV-817 follow-up): The current backend returns a raw array. The wrapped
// `{items: [...]}` branch is speculative forward-compat and should be removed
// once the backend shape is frozen — a dual-shape union silently masks schema
// drift if the backend ships the wrapped form without a coordinated migration.
const B2BOpportunityListResponseSchema = z.union([
  z.array(B2BOpportunitySchema),
  z.object({ items: z.array(B2BOpportunitySchema) }),
]);

const unwrapOpportunities = (data: unknown): B2BOpportunity[] => {
  const parsed = B2BOpportunityListResponseSchema.parse(data);
  return Array.isArray(parsed) ? parsed : parsed.items;
};

// GET /hlx/job-responses/b2b-listing-responses is owned by the MCV-817
// response workspace and returns a raw array, so keep this schema strict.
const B2BListingResponseListResponseSchema = z.array(B2BListingResponseSchema);

export const API_CALLS = {
  getOpportunities: async (
    filter: B2BOpportunityFilter = 'all',
    signal?: AbortSignal,
    includeDeclined = false,
    // [MCV-1889] Which Buying | Selling tab; omitted, the server returns today's rows.
    side?: B2BResponsesSide
  ): Promise<{ items: B2BOpportunity[]; totalCount: number }> => {
    const data = await getData('b2bJobOpportunities', {
      signal,
      params: { status: filter, includeDeclined: String(includeDeclined), ...(side && { side }) },
    });
    const items = unwrapOpportunities(data);
    return { items, totalCount: items.length };
  },

  getListingResponses: async ({
    filter = 'all',
    listingId,
    includeDeclined = false,
    side,
    signal,
  }: {
    filter?: B2BOpportunityFilter;
    listingId?: number;
    includeDeclined?: boolean;
    side?: B2BResponsesSide;
    signal?: AbortSignal;
  } = {}): Promise<B2BListingResponse[]> => {
    const data = await getData('b2bListingResponses', {
      signal,
      params: {
        status: filter,
        listingId: listingId?.toString(),
        // The listing owner may decline in any open state (MCV-1850).
        allowReject: 'true',
        includeDeclined: String(includeDeclined),
        ...(side && { side }),
      },
    });
    return B2BListingResponseListResponseSchema.parse(data);
  },

  getMessages: async (responseId: number, signal?: AbortSignal): Promise<B2BMessage[]> => {
    const data = await getData('jobMessages', {
      signal,
      pathParams: { responseId: responseId.toString() },
    });
    return z
      .array(B2BMessageSchema.refine((message) => message.responseId === responseId))
      .parse(data);
  },

  sendMessage: async (
    responseId: number,
    messageBody: string,
    signal?: AbortSignal
  ): Promise<B2BMessage> => {
    const data = await postData('jobMessages', {
      signal,
      pathParams: { responseId: responseId.toString() },
      data: { messageBody },
    });
    return B2BMessageSchema.refine((message) => message.responseId === responseId).parse(data);
  },

  acceptOpportunity: async (
    responseId: number,
    expectedNegotiationId: number | undefined,
    signal?: AbortSignal,
    // [MCV-1858] The B2B payer's authorization of the recurring platform-fee schedule.
    feeAuthorization?: FeeAuthorization
  ): Promise<void> => {
    await postData('hlxAccept', {
      signal,
      pathParams: { responseId: responseId.toString() },
      data: { expectedNegotiationId, ...(feeAuthorization ? { feeAuthorization } : {}) },
    });
  },

  withdrawOpportunity: async (responseId: number, signal?: AbortSignal): Promise<void> => {
    await patchData('jobOpportunityStatus', {
      signal,
      pathParams: { responseId: responseId.toString() },
      data: { status: 'WITHDRAWN' },
    });
  },

  rejectOpportunity: async (responseId: number, signal?: AbortSignal): Promise<void> => {
    await patchData('jobOpportunityStatus', {
      signal,
      pathParams: { responseId: responseId.toString() },
      data: { status: 'REJECTED' },
    });
  },

  getNegotiationChain: async (
    responseId: number,
    signal?: AbortSignal
  ): Promise<B2BNegotiationChainRow[]> => {
    const data = await getData('msaasNegotiationByResponse', {
      signal,
      pathParams: { responseId: responseId.toString() },
    });
    return z.array(B2BNegotiationChainRowSchema).parse(data);
  },
};
